import { Component, Input, OnInit } from '@angular/core';
import { IAlbum, Lightbox } from 'ngx-lightbox';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import EmployeeHelper from 'src/app/helpers/employee-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Asset from 'src/app/model/asset';
import { NotExecutedHistoryItem } from 'src/app/model/history-item/not-executed-history-item';
import Employee from 'src/app/model/product/employee';
import Product from 'src/app/model/product/product';
import { Adjustment } from 'src/app/model/project/adjustment';
import { Repo } from 'src/app/model/project/repo';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import {
  ImmoExecutionInfo,
  ImmoExecutionInfoControllerRequestService,
} from 'src/app/services/api/immo-execution-info/immo-execution-info-controller-request.service';
import {
  ProjectDisplayObservableService,
  TourConfigurationChangedEvent,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { FileHelper } from '../../../../../helpers/file-helper';

@Component({
  selector: 'client-adjustment-execution-info',
  templateUrl: './client-adjustment-execution-info.component.html',
  styleUrls: ['./client-adjustment-execution-info.component.scss'],
})
export class ClientAdjustmentExecutionInfoComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  products: Product[];
  @Input()
  adjustment: Adjustment;
  @Input()
  client: boolean = false;

  projectHasIPBoards: boolean = false;

  executionInfoHeader: string;
  placerNames: string;
  executionInfoDateLabel: string;
  executionInfoDateDisplay: string;
  adjustmentExecutionAssets: Asset[];

  notExecuted: boolean = false;
  showNotExecutedHistoryItemInfo: boolean = false;
  notExecutedHistoryItem: NotExecutedHistoryItem;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private immoExecutionInfoControllerRequestService: ImmoExecutionInfoControllerRequestService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.projectHasIPBoards =
      CollectionHelper.isPopulated(this.products) &&
      this.products.some((p) => ProductHelper.isIPBoard(p));

    const executedForClient =
      ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(
        this.adjustment.status
      );
    this.notExecuted =
      !executedForClient &&
      ProjectHelper.NOT_EXECUTED_STATUSES.includes(this.adjustment.status);
    this.showNotExecutedHistoryItemInfo =
      this.notExecuted ||
      (this.adjustment.projectPlacement != null &&
        this.adjustment.projectPlacement.placementDone === false);
    this.executionInfoHeader = executedForClient
      ? 'Uitgevoerd'
      : this.notExecuted || this.showNotExecutedHistoryItemInfo
      ? 'Niet uitgevoerd'
      : this.adjustment.status === 'Uitgesteld'
      ? 'Uitgesteld'
      : 'Uitvoering';
    this.executionInfoDateLabel = executedForClient
      ? 'Uitgevoerd op'
      : 'Ingepland op';
    this.handleAdjustmentExecutionAssets();

    if (!this.projectHasIPBoards) {
      this.handleNonImmoAdjustmentPlacementInfo(executedForClient);
    }

    if (this.projectHasIPBoards && this.client) {
      this.initAdjustmentTourInfo();
    } else if (this.projectHasIPBoards && !this.client) {
      this.addSubscription(
        this.projectDisplayObservableService.getTourConfigurationChangedSubscribable(),
        (event: TourConfigurationChangedEvent) => {
          if (event.subProjectId === this.adjustment.id) {
            this.processImmoExecutionInfo(
              event.assignedToTour,
              event.placer,
              event.date,
              event.notExecutedHistoryItem
            );
          }
        }
      );
    }
  }

  private handleAdjustmentExecutionAssets(): void {
    if (
      !this.showNotExecutedHistoryItemInfo &&
      this.adjustment.projectPlacement != null &&
      CollectionHelper.isPopulated(
        this.adjustment.projectPlacement.projectPlacementAssets
      )
    ) {
      const adjustmentExecutionAssets = [];
      this.adjustment.projectPlacement.projectPlacementAssets.forEach((pa) => {
        if (
          !pa.archived &&
          pa.asset != null &&
          FileHelper.isImage(pa.asset) &&
          pa.formSection === 'adjustmentPlacementSuccess'
        ) {
          adjustmentExecutionAssets.push(pa.asset);
        }
      });
      this.adjustmentExecutionAssets = adjustmentExecutionAssets;
    }
  }

  private handleNonImmoAdjustmentPlacementInfo(
    executedForClient: boolean
  ): void {
    if (
      this.adjustment.type === SUB_PROJECT_TYPES.REPO &&
      !(<Repo>this.adjustment).repoByInpress
    ) {
      this.placerNames = 'Andere partij dan Inpress';
    } else if (
      this.adjustment.projectPlacement != null &&
      CollectionHelper.isPopulated(this.adjustment.projectPlacement.placers)
    ) {
      this.placerNames = StringHelper.join(
        this.adjustment.projectPlacement.placers.map((pl) =>
          EmployeeHelper.getName(pl)
        ),
        ', '
      );
    }
    this.executionInfoDateDisplay = executedForClient
      ? DateHelper.formatDate(this.adjustment.currentExecutionDate)
      : DateHelper.formatDate(this.adjustment.currentConfirmedPlacementDate);
  }

  private initAdjustmentTourInfo(): void {
    this.immoExecutionInfoControllerRequestService
      .getImmoExecutionInfo(this.adjustment.id)
      .then((immoExecutionInfo: ImmoExecutionInfo) => {
        if (immoExecutionInfo != null) {
          this.processImmoExecutionInfo(
            immoExecutionInfo.assignedToTour,
            immoExecutionInfo.placer,
            immoExecutionInfo.date,
            immoExecutionInfo.notExecutedHistoryItem
          );
        } else {
          // fallback
          const executedForClient =
            ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(
              this.adjustment.status
            );
          this.handleNonImmoAdjustmentPlacementInfo(executedForClient);
        }
      });
  }

  private processImmoExecutionInfo(
    assignedToTour: boolean,
    placer: Employee,
    date: Date,
    notExecutedHistoryItem: NotExecutedHistoryItem
  ) {
    if (assignedToTour) {
      this.placerNames = EmployeeHelper.getName(placer);
      this.executionInfoDateDisplay = DateHelper.formatDate(date);
    } else if (
      ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(
        this.adjustment.status
      )
    ) {
      if (
        this.adjustment.type === SUB_PROJECT_TYPES.REPO &&
        !(<Repo>this.adjustment).repoByInpress
      ) {
        this.placerNames = 'Andere partij dan Inpress';
      }
      this.executionInfoDateDisplay = DateHelper.formatDate(
        this.adjustment.currentExecutionDate
      );
    }
    if (this.showNotExecutedHistoryItemInfo) {
      this.notExecutedHistoryItem = notExecutedHistoryItem;
      if (
        notExecutedHistoryItem != null &&
        notExecutedHistoryItem.assets != null
      ) {
        this.adjustmentExecutionAssets = notExecutedHistoryItem.assets.map(
          (a) => a.asset
        );
      }
    }
  }

  openAdjustmentExecutionAssetsLightbox(): void {
    if (!CollectionHelper.isPopulated(this.adjustmentExecutionAssets)) {
      return;
    }

    const album: IAlbum[] = this.adjustmentExecutionAssets.map((a) => {
      return {
        src: '/api/assets/' + a.uuid,
        thumb: '/api/assets/' + a.uuid,
        caption: 'Uitvoering',
      };
    });

    this._lightbox.open(album);
  }
}
